import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Page,
  AvatarLarge,
  NamedLink,
  Reviews,
  ButtonTabNavHorizontal,
  IconSuccess,
  EnglishFlag,
  FrenchFlag,
  GermanFlag,
  ItalianFlag,
  Logo,
  ListingCard,
  LoadMore,
  ProfileActivities,
  UserType,
  ExternalLink,
  LayoutSingleColumn,
} from '../../components';
import classNames from 'classnames';
import ReviewsModal from './ReviewsModal';
import { queryUserListings, queryUserReviews } from './ProfilePage.duck';
import { dateFormat } from '../../util/dates';
import { instagramPageURL } from '../../util/urlHelpers';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { capitalizeWord } from '../../util/richText';
import { useConfiguration } from '../../context/configurationContext';

import css from './ProfilePage.module.css';

const getLanguageFlag = language => {
  switch (language) {
    case 'english':
      return <EnglishFlag className={css.flagIcon} />;
    case 'french':
      return <FrenchFlag className={css.flagIcon} />;
    case 'german':
      return <GermanFlag className={css.flagIcon} />;
    case 'italian':
      return <ItalianFlag className={css.flagIcon} />;
    default:
      break;
  }

  return null;
};

const getIconSuccessVerifyType = isVerified => (isVerified ? 'verified' : 'notverified');

const panelMediumWidth = 50;
const panelLargeWidth = 62.5;
const cardRenderSizes = [
  '(max-width: 767px) 100vw',
  `(max-width: 1023px) ${panelMediumWidth}vw`,
  `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
  `${panelLargeWidth / 3}vw`,
].join(', ');

const ProfilePageComponent = props => {
  const {
    scrollingDisabled,
    currentUser,
    user,
    userShowError,
    intl,
    listings,
    listingsPagination,
    listingsQueryParams,
    queryListingsInProgress,
    queryUserListings,
    reviews,
    queryReviewsError,
    reviewsQueryParams,
    queryReviewsInProgress,
    reviewsPagination,
    queryUserReviews,
    totalHostReviews,
    totalGuestsReviews,
  } = props;

  const config = useConfiguration();

  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);

  const ensuredCurrentUser = useMemo(() => ensureCurrentUser(currentUser), [currentUser]);
  const profileUser = useMemo(() => ensureUser(user), [user]);
  const isCurrentUser = useMemo(
    () =>
      ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid,
    [ensuredCurrentUser.id, profileUser.id]
  );

  const { profile, createdAt } = useMemo(() => profileUser.attributes, [profileUser.attributes]);
  const { publicData, displayName, bio, metadata } = useMemo(() => profile || {}, [profile]);
  const { languages, locations, activities = {}, instagramFeed, instagram } = useMemo(
    () => publicData || {},
    [publicData]
  );

  const { isInstagramVerified, isEmailVerified, isStripeVerified, certificates = [] } = useMemo(
    () => metadata || {},
    [metadata]
  );

  const memberSinceYear = useMemo(
    () => (createdAt instanceof Date ? createdAt.getFullYear() : null),
    [createdAt]
  );

  const totalReviews = useMemo(() => totalHostReviews + totalGuestsReviews, [
    totalHostReviews,
    totalGuestsReviews,
  ]);

  const messages = useMemo(
    () => ({
      email: intl.formatMessage({ id: 'General.email' }),
      instagram: intl.formatMessage({ id: 'General.instagram' }),
      identity: intl.formatMessage({ id: 'General.identity' }),
      reviews: intl.formatMessage({ id: 'General.reviews' }).toLowerCase(),
      language: intl.formatMessage({ id: 'General.language' }),
      locations: intl.formatMessage({ id: 'General.locations' }).toLowerCase(),
      listings: intl.formatMessage({ id: 'General.listings' }).toLowerCase(),
      bio: intl.formatMessage({ id: 'General.bio' }).toLowerCase(),
      schemaTitle: intl.formatMessage(
        {
          id: 'ProfilePage.schemaTitle',
        },
        {
          name: displayName,
          siteTitle: config.marketplaceName,
        }
      ),
    }),
    [intl, displayName, config]
  );

  const reviewsOfProvider = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER),
    [reviews]
  );

  const reviewsOfCustomer = useMemo(
    () => reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER),
    [reviews]
  );

  const showOfProviderReviews = useCallback(() => {
    queryUserReviews({ ...reviewsQueryParams, type: 'ofCustomer' });
    setShowReviewsType(REVIEW_TYPE_OF_PROVIDER);
  }, [queryUserReviews, reviewsQueryParams]);

  const showOfCustomerReviews = useCallback(() => {
    queryUserReviews({ ...reviewsQueryParams, type: 'ofProvider' });
    setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER);
  }, [queryUserReviews, reviewsQueryParams]);

  const reviewTabs = useMemo(
    () => [
      {
        text: (
          <h3 className={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsOfProviderTitle"
              values={{ count: totalHostReviews }}
            />
          </h3>
        ),
        selected: showReviewsType === REVIEW_TYPE_OF_PROVIDER,
        onClick: showOfProviderReviews,
      },
      {
        text: (
          <h3 className={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsOfCustomerTitle"
              values={{ count: totalGuestsReviews }}
            />
          </h3>
        ),
        selected: showReviewsType === REVIEW_TYPE_OF_CUSTOMER,
        onClick: showOfCustomerReviews,
      },
    ],
    [
      showOfCustomerReviews,
      showOfProviderReviews,
      showReviewsType,
      totalGuestsReviews,
      totalHostReviews,
    ]
  );

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={messages.schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: messages.schemaTitle,
        dateCreated: createdAt ? new Date(createdAt).toISOString() : undefined,
        mainEntity: {
          '@context': 'http://schema.org',
          '@type': 'Person',
          name: displayName,
          identifier: profileUser?.id?.uuid,
        },
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="ProfilePage" />}
        footer={<FooterContainer />}
        mainColumnClassName={css.container}
      >
        <div className={css.aside}>
          <div className={css.asideContent}>
            <div className={css.asideHeader}>
              <AvatarLarge className={css.avatar} user={user} disableProfileLink />
              <div className={css.userInfoCard}>
                <UserType user={profileUser} showFullText className={css.userType} />

                <div className={css.userInfoContainer}>
                  <ul className={css.userInfoList}>
                    <li>
                      <IconSuccess
                        className={css.iconSuccess}
                        tagFill={getIconSuccessVerifyType(isEmailVerified)}
                      />
                      {messages.email}
                    </li>
                    {instagram && (
                      <li>
                        <IconSuccess
                          className={css.iconSuccess}
                          tagFill={getIconSuccessVerifyType(isInstagramVerified)}
                        />
                        <ExternalLink href={instagramPageURL(instagram)}>
                          {messages.instagram}
                        </ExternalLink>
                      </li>
                    )}
                    <li>
                      <IconSuccess
                        className={css.iconSuccess}
                        tagFill={getIconSuccessVerifyType(isStripeVerified)}
                      />
                      {messages.identity}
                    </li>
                  </ul>
                  <ul className={css.userInfoListRight}>
                    <li>
                      {totalReviews} {capitalizeWord(messages.reviews)}
                    </li>
                    <li>{messages.language}</li>
                    <li>
                      {languages?.map(lang => <span key={lang}>{getLanguageFlag(lang)}</span>)}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <h1 className={css.desktopHeading}>
              <FormattedMessage
                id="ProfilePage.desktopHeading"
                values={{
                  name: <span className={css.emphasisedText}>{displayName}!</span>,
                }}
              />
              {isCurrentUser ? (
                <NamedLink className={css.editProfileLink} name="ProfileSettingsPage">
                  <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
                </NamedLink>
              ) : null}
            </h1>

            <span className={css.memberInfo}>
              <FormattedMessage
                id="ProfilePage.memberSince"
                values={{
                  year: memberSinceYear,
                }}
              />
            </span>
          </div>
        </div>
        {userShowError ? (
          <p className={css.error}>
            <FormattedMessage id="ProfilePage.loadingDataFailed" />
          </p>
        ) : (
          <div className={css.mainContent}>
            <ProfileActivities
              className={css.profileSection}
              activities={activities}
              certificates={certificates}
              intl={intl}
            />
            {locations ? (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id={`General.locations`} />
                </h3>
                {!!locations.length ? (
                  locations.map((location, i) => (
                    <div key={location + i} className={classNames(css.locationCard)}>
                      <div className={css.locationName}>
                        <span className={css.activityIconWrapper}>
                          <Logo format={'mobile'} className={css.crionMarker} />
                        </span>
                        {location}
                      </div>
                    </div>
                  ))
                ) : (
                  <span className={css.emptySection}>
                    <FormattedMessage id="General.noItems" values={{ items: messages.locations }} />
                  </span>
                )}
              </div>
            ) : null}

            <div className={css.profileSection}>
              <h3>
                <FormattedMessage id={`General.bio`} />
              </h3>
              {bio ? (
                <p className={css.bio}>{bio}</p>
              ) : (
                <span className={css.emptySection}>
                  <FormattedMessage id="General.noItems" values={{ items: messages.bio }} />
                </span>
              )}
            </div>
            {instagramFeed?.length > 0 && (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id={`General.instagramFeed`} />
                </h3>
                <div className={css.instagramFeed}>
                  {instagramFeed?.map(data => (
                    <ExternalLink key={data.id} href={data.link}>
                      <img title={data.caption} src={data.image} alt="" />
                      <span>{dateFormat(data.timestamp)}</span>
                    </ExternalLink>
                  ))}
                </div>
              </div>
            )}

            {listings && (
              <div className={css.profileSection}>
                <h3>
                  <FormattedMessage id={`General.listings`} />
                </h3>
                {!!listings.length ? (
                  <>
                    {listings.map(l => (
                      <ListingCard
                        className={css.listingCard}
                        key={l.id.uuid}
                        listing={l}
                        currentUser={currentUser}
                        renderSizes={cardRenderSizes}
                        setActiveListing={() => {}}
                        config={config}
                      />
                    ))}

                    <LoadMore
                      onClickLoadMore={params => queryUserListings(params)}
                      pagination={listingsPagination}
                      fetchParams={listingsQueryParams}
                      inProgress={queryListingsInProgress}
                    />
                  </>
                ) : (
                  <span className={css.emptySection}>
                    <FormattedMessage id="General.noItems" values={{ items: messages.listings }} />
                  </span>
                )}
              </div>
            )}

            <div className={css.desktopReviews}>
              <h3>
                <FormattedMessage id="General.reviews" />
              </h3>

              {totalReviews ? (
                <>
                  <ButtonTabNavHorizontal
                    tabRootClassName={css.tab}
                    className={css.desktopReviewsTabNav}
                    tabs={reviewTabs}
                  />

                  {queryReviewsError ? (
                    <p className={css.error}>
                      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
                    </p>
                  ) : null}

                  {showReviewsType === REVIEW_TYPE_OF_PROVIDER ? (
                    reviewsOfCustomer.length === 0 && queryReviewsInProgress ? (
                      <div>
                        <FormattedMessage
                          id="General.loadingItems"
                          values={{ items: messages.reviews }}
                        />
                      </div>
                    ) : reviewsOfCustomer.length ? (
                      <Reviews
                        reviews={reviewsOfCustomer.slice(0, 2)}
                        className={css.reviews}
                        reviewCardClassName={css.reviewCard}
                      />
                    ) : (
                      <span className={css.emptySection}>
                        <FormattedMessage
                          id="General.noItems"
                          values={{ items: messages.reviews }}
                        />
                      </span>
                    )
                  ) : reviewsOfProvider.length === 0 && queryReviewsInProgress ? (
                    <div>
                      <FormattedMessage
                        id="General.loadingItems"
                        values={{ items: messages.reviews }}
                      />
                    </div>
                  ) : reviewsOfProvider.length ? (
                    <Reviews
                      reviews={reviewsOfProvider.slice(0, 2)}
                      className={css.reviews}
                      reviewCardClassName={css.reviewCard}
                    />
                  ) : (
                    <span className={css.emptySection}>
                      <FormattedMessage id="General.noItems" values={{ items: messages.reviews }} />
                    </span>
                  )}
                </>
              ) : (
                <span className={css.emptySection}>
                  <FormattedMessage id="General.noItems" values={{ items: messages.reviews }} />
                </span>
              )}
            </div>
            <ReviewsModal
              reviews={reviews}
              fetchParams={reviewsQueryParams}
              pagination={reviewsPagination}
            />
          </div>
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  reviews: [],
  queryReviewsError: null,
};

const { bool, arrayOf } = PropTypes;

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    reviews,
    queryReviewsError,
    listings,
    listingsPagination,
    listingsQueryParams,
    queryListingsInProgress,
    reviewsQueryParams,
    reviewsPagination,
    totalHostReviews,
    totalGuestsReviews,
    queryReviewsInProgress,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);

  const user = userMatches.length === 1 ? userMatches[0] : null;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    listings,
    reviewsQueryParams,
    reviewsPagination,
    userShowError,
    reviews,
    queryReviewsError,
    listingsPagination,
    listingsQueryParams,
    queryListingsInProgress,
    queryReviewsInProgress,
    totalHostReviews,
    totalGuestsReviews,
  };
};

const mapDispatchToProps = dispatch => ({
  queryUserReviews: params => dispatch(queryUserReviews(params)),
  queryUserListings: params => dispatch(queryUserListings(params)),
});

const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
